import { AiEngineList, MenuList } from "../../context/juno/VideoContext";

// バリデーション関数の戻り値の型
interface ValidationResult {
  isValid: boolean;
  errorKey: string | null;
}

// 動画メタデータの型定義
interface VideoMetadata {
  fps: number;
  duration: number;
}

/**
 * 動画生成のバリデーション
 */
export const validateVideoParams = (params: {
  aiEngine: AiEngineList;
  modelVersion: string;
  menu: MenuList;
  prompt: string;
  prompt2?: string;
  image1?: string;
  image2?: string;
  image3?: string;
  video1?: string;
  video1Metadata?: VideoMetadata | null;
  audio1?: string;
  scale?: number;
  extendId?: string;
}): ValidationResult => {
  console.log("validateVideoParams", params);
  const {
    aiEngine,
    modelVersion,
    menu,
    prompt,
    image1 = "",
    image2 = "",
    image3 = "",
    video1 = "",
    video1Metadata,
    audio1 = "",
    prompt2 = "",
  } = params;

  // BYTEDANCE、SYNC、UPSCALERの場合はプロンプト入力を不要とする
  if (!["BYTEDANCE", "SYNC", "REALESRGAN", "TOPAZLABS"].includes(aiEngine as string)) {
    if (prompt.trim().length === 0) {
      return { isValid: false, errorKey: "juno.video.error.required.prompt" };
    }

    // 512文字以上の場合
    if (prompt.length > 512 && aiEngine === ("RUNWAY" as AiEngineList)) {
      return { isValid: false, errorKey: "juno.video.error.max.prompt" };
    }

    if (prompt.length > 1000 && aiEngine !== ("RUNWAY" as AiEngineList)) {
      return { isValid: false, errorKey: "juno.video.error.max.prompt" };
    }
  }

  // BYTEDANCE、SYNC、UPSCALERの場合は動画/音声ファイルのチェック
  if (aiEngine === ("BYTEDANCE" as AiEngineList) || aiEngine === ("SYNC" as AiEngineList)) {
    if (!video1) {
      return { isValid: false, errorKey: "juno.video.error.required.video" };
    }
    if (!audio1) {
      return { isValid: false, errorKey: "juno.video.error.required.audio" };
    }
  } else if (aiEngine === ("REALESRGAN" as AiEngineList)) {
    if (!video1) {
      return { isValid: false, errorKey: "juno.video.error.required.video" };
    }

    // REALESRGANの場合、1000フレーム以上はエラー
    if (modelVersion === "REALESRGAN" && video1Metadata) {
      const totalFrames = Math.floor(video1Metadata.fps * video1Metadata.duration);
      if (totalFrames >= 1000) {
        return { isValid: false, errorKey: "juno.video.error.max.frames" };
      }
    }
  } else if (menu === ("i2v" as MenuList)) {
    if (aiEngine === ("RUNWAY" as AiEngineList)) {
      if (image1.trim().length === 0 && image2.trim().length === 0) {
        return { isValid: false, errorKey: "juno.video.error.required.image" };
      }
    } else if (aiEngine === ("VIDU" as AiEngineList)) {
      if (modelVersion === "VIDU_2_0_REF_720P") {
        if (image1.trim().length === 0 && image2.trim().length === 0 && image3.trim().length === 0) {
          return { isValid: false, errorKey: "juno.video.error.required.image" };
        }
      } else if (modelVersion === "VIDU_2_0_720P" || modelVersion === "VIDU_2_0_1080P") {
        if (image1.trim().length === 0) {
          return { isValid: false, errorKey: "juno.video.error.required.onlyLastImage" };
        }
      }
    } else if (aiEngine === ("LUMA" as AiEngineList)) {
      // Extend機能を使用する場合、画像は必須ではない
      if (!params.extendId && image1.trim().length === 0 && image2.trim().length === 0) {
        return { isValid: false, errorKey: "juno.video.error.required.image" };
      }
    } else if (modelVersion === "FIRST_TO_LAST") {
      if (image1.trim().length === 0 || image2.trim().length === 0) {
        return { isValid: false, errorKey: "juno.video.error.required.image" };
      }
    } else {
      if (!params.extendId && image1.trim().length === 0) {
        return { isValid: false, errorKey: "juno.video.error.required.image" };
      }
    }
  } else if (menu === ("v2v" as MenuList)) {
    if (video1.trim().length === 0) {
      return { isValid: false, errorKey: "juno.video.error.required.video" };
    }
    if (modelVersion === "V2_SWAPS" && (!prompt2 || prompt2.trim().length === 0)) {
      return { isValid: false, errorKey: "juno.video.error.required.swap" };
    }
    if (modelVersion.includes("V2_ADDITIONS") && image1.trim().length === 0) {
      return { isValid: false, errorKey: "juno.video.error.required.image" };
    }
  }

  // サイズチェック
  const maxImageSizeInBytes = 16000000; // 16MB
  const maxVideoSizeInBytes = 8000000; // 8MB (V2_SWAPS, V2_ADDITIONS用)

  // V2_SWAPSまたはV2_ADDITIONSモデルを使用している場合の動画サイズチェック
  if (aiEngine === ("PIKA" as AiEngineList) && (modelVersion === "V2_SWAPS" || modelVersion === "V2_ADDITIONS")) {
    if (video1.trim().length > 0 && validateFileSize(video1, maxVideoSizeInBytes)) {
      return { isValid: false, errorKey: "juno.video.error.max.smallVideoSize" };
    }
  }

  // 画像サイズチェック
  if (aiEngine === ("RUNWAY" as AiEngineList)) {
    if (image1.trim().length > 0 && validateFileSize(image1, maxImageSizeInBytes)) {
      return { isValid: false, errorKey: "juno.input.uploadError.size" };
    }
    if (image2.trim().length > 0 && validateFileSize(image2, maxImageSizeInBytes)) {
      return { isValid: false, errorKey: "juno.input.uploadError.size" };
    }
  } else {
    if (image1.trim().length > 0 && validateFileSize(image1, maxImageSizeInBytes)) {
      return { isValid: false, errorKey: "juno.input.uploadError.size" };
    }
  }

  return { isValid: true, errorKey: null };
};

/**
 * ファイルサイズのバリデーション
 */
export const validateFileSize = (file: string, maxSize: number): boolean => {
  if (file.trim().length === 0) return false; // 空の場合は無視
  const base64Length = file.length - (file.indexOf(",") + 1); // ヘッダー部分を除外
  const padding = file.endsWith("==") ? 2 : file.endsWith("=") ? 1 : 0;
  const actualFileSizeInBytes = (base64Length * 3) / 4 - padding; // 実際のサイズをバイトに換算
  return actualFileSizeInBytes > maxSize;
};

/**
 * Disable状態の判定関数
 */

// プロンプト入力フィールドが無効かどうかを判定
export const isPromptInputDisabled = (aiEngine: AiEngineList, menu: MenuList): boolean => {
  return (
    menu === "upscale" ||
    menu === "lipsync" ||
    ["BYTEDANCE", "SYNC", "REALESRGAN", "TOPAZLABS"].includes(aiEngine as string)
  );
};

// ループスイッチが無効かどうかを判定（特にRAY_2モデルでの制御）
export const isLoopSwitchDisabled = (modelVersion: string, menu: MenuList, image1: string, image2: string): boolean => {
  return modelVersion.includes("RAY") && menu === ("i2v" as MenuList) && image1 !== "" && image2 !== "";
};

// RAY_2モデルで動画の長さを5秒に強制するかどうかを判定
export const shouldForceDurationToFiveSeconds = (modelVersion: string, menu: MenuList): boolean => {
  return modelVersion === "RAY_1_6" && menu === ("i2v" as MenuList);
};

// 各AIエンジンで動画の長さを制限する条件を判定する共通関数
export const shouldForceDuration = (
  aiEngine: AiEngineList,
  modelVersion: string,
  menu: MenuList
): { shouldForce: boolean; forcedValue: number } => {
  // PIKA (V2_2以外)
  if (aiEngine === ("PIKA" as AiEngineList) && !(modelVersion === "V2_2_720P" || modelVersion === "V2_2_1080P")) {
    return { shouldForce: true, forcedValue: 5 };
  }

  // PIXVERSE (1080p以上のモデル)
  if (
    aiEngine === ("PIXVERSE" as AiEngineList) &&
    ["V3_5_1080P", "V3_5_TRANSITION_1080P", "V4_1080P", "V3_5_FAST", "V4_FAST"].includes(modelVersion)
  ) {
    return { shouldForce: true, forcedValue: 5 };
  }

  // VIDU
  if (
    aiEngine === ("VIDU" as AiEngineList) &&
    (modelVersion === "VIDU_2_0_REF_720P" || modelVersion === "VIDU_2_0_1080P")
  ) {
    return { shouldForce: true, forcedValue: 4 };
  }

  return { shouldForce: false, forcedValue: 0 };
};

// スライダーが無効化されるかどうかを判定する共通関数
export const isDurationSliderDisabledForEngine = (
  aiEngine: AiEngineList,
  modelVersion: string,
  menu: MenuList,
  image1: string = "",
  image2: string = ""
): boolean => {
  // LUMA
  if (aiEngine === ("LUMA" as AiEngineList)) {
    return modelVersion === "RAY_1_6";
  }

  // KLING (2枚の画像がセットされている場合)
  if (aiEngine === ("KLING" as AiEngineList)) {
    return modelVersion.includes("PRO") && menu === ("i2v" as MenuList) && image1 !== "" && image2 !== "";
  }

  // PIKA (V2_2以外のモデル)
  if (aiEngine === ("PIKA" as AiEngineList)) {
    return !(modelVersion === "V2_2_720P" || modelVersion === "V2_2_1080P");
  }

  // PIXVERSE (1080p以上のモデル)
  if (aiEngine === ("PIXVERSE" as AiEngineList)) {
    return ["V3_5_TRANSITION_1080P", "V3_5_1080P", "V4_1080P", "V3_5_FAST", "V4_FAST"].includes(modelVersion);
  }

  // VIDU
  if (aiEngine === ("VIDU" as AiEngineList)) {
    return modelVersion === "VIDU_2_0_REF_720P" || modelVersion === "VIDU_2_0_1080P";
  }

  return false;
};

// 画像アップロードモード
export enum ImageUploadMode {
  SINGLE = "SINGLE", // 1枚のみ
  DUAL = "DUAL", // 2枚（最初と最後）
  TRIPLE = "TRIPLE", // 3枚（シーン）
  NONE = "NONE", // 画像不要
}

// 各エンジンの最適な画像アップロードモードを判定
export const getImageUploadMode = (aiEngine: AiEngineList, modelVersion: string, menu: MenuList): ImageUploadMode => {
  if (menu !== ("i2v" as MenuList)) {
    return ImageUploadMode.NONE;
  }

  // RUNWAY
  if (aiEngine === ("RUNWAY" as AiEngineList)) {
    if (modelVersion === "GEN4_TURBO") {
      return ImageUploadMode.SINGLE;
    } else {
      return ImageUploadMode.DUAL;
    }
  }

  // LUMA
  if (aiEngine === ("LUMA" as AiEngineList)) {
    if (modelVersion === "V1_5") {
      return ImageUploadMode.SINGLE;
    } else if (["RAY_2", "RAY_2_1080P", "RAY_2_FLASH", "RAY_2_FLASH_1080P"].includes(modelVersion)) {
      return ImageUploadMode.DUAL;
    }
  }

  // KLING
  if (aiEngine === ("KLING" as AiEngineList)) {
    if (modelVersion === "V1_6_PRO") {
      return ImageUploadMode.NONE;
    } else if (modelVersion.includes("PRO")) {
      return ImageUploadMode.DUAL;
    } else {
      return ImageUploadMode.SINGLE;
    }
  }

  // PIKA
  if (aiEngine === ("PIKA" as AiEngineList)) {
    if (modelVersion.includes("SCENES")) {
      return ImageUploadMode.TRIPLE;
    } else {
      return ImageUploadMode.SINGLE;
    }
  }

  // PIXVERSE
  if (aiEngine === ("PIXVERSE" as AiEngineList)) {
    if (modelVersion.includes("TRANSITION")) {
      return ImageUploadMode.DUAL;
    } else {
      return ImageUploadMode.SINGLE;
    }
  }

  // VIDU
  if (aiEngine === ("VIDU" as AiEngineList)) {
    if (modelVersion === "VIDU_2_0_REF_720P") {
      return ImageUploadMode.TRIPLE;
    } else {
      return ImageUploadMode.DUAL;
    }
  }

  return ImageUploadMode.SINGLE; // デフォルト
};
